import { useCallback, useEffect, useId, useRef, useState } from "react";
import styles from "./index.module.scss";

import AiSearchBox from "./AiSearchBox/AiSearchBox";
import BathsOption from "./options/BathsOptions/BathsOptions";
import BedsOption from "./options/BedsOption/BedsOptions";
import PriceOption from "./options/PriceOption/PriceOption";
import SquareFeetOption from "./options/SquareFeetOption/SquareFeetOption";
import AcresOption from "./options/AcresOption/AcresOption";
import PropertyTypeOption from "./options/PropertyTypeOptions/PropertyTypeOption";
import PropertyFeatureOption from "./options/PropertyFeaturesOption/PropertyFeaturesOption";

import Button from "../../base/Button/Button";
import SaveSearchModal from "../../SaveSearchModal/SaveSearchModal";

import { ReactComponent as CarrotDownIcon } from "../../../assets/icons/ui/caret-down.svg";

import { hasActiveFilters, useSearchStore } from "../../../store/searchStore";
import { PropertyFeature, PropertyType } from "../../../types/constants";
import { AlertFrequency, buildShareUrl, saveSearch as saveSearchApi } from "../../../services/api";
import { mapConfig, loginRedirectUrl } from "../../../config/mapConfig";
// Heading removed: Results now owns the count + sort header.
// Search button removed: filters auto-apply (live filtering).

import { ReactComponent as StarIcon } from "../../../assets/icons/ui/star.svg";

const ShareIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="18" cy="5" r="3" />
    <circle cx="6" cy="12" r="3" />
    <circle cx="18" cy="19" r="3" />
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
  </svg>
);

const CheckIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const XIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const FiltersIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1="4" y1="6" x2="20" y2="6" />
    <line x1="7" y1="12" x2="17" y2="12" />
    <line x1="10" y1="18" x2="14" y2="18" />
  </svg>
);

export default function SearchForm() {
  // Mobile only: the filters sit behind a toggle. The CSS ignores this
  // state above the md breakpoint, so desktop always shows them.
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filtersId = `search-filters-${useId()}`;
  const actionsId = `search-actions-${useId()}`;
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveError, setSaveError] = useState("");
  const copyTimer = useRef<number | null>(null);
  const saveTimer = useRef<number | null>(null);

  const formData = useSearchStore((s) => s.formData);
  const setField = useSearchStore((s) => s.setField);
  const setRange = useSearchStore((s) => s.setRange);
  const clearAllFilters = useSearchStore((s) => s.clearAllFilters);
  // Same test as the x in the search box, minus its unsent-text case.
  const canClear = useSearchStore(
    (s) =>
      hasActiveFilters(s.formData) ||
      s.mapBounds !== null ||
      !!s.aiQuery ||
      !!s.aiSummary ||
      !!s.aiError
  );

  // Controls behind the mobile toggle that narrow the results.
  const filterCount = [
    formData.price.min != null || formData.price.max != null,
    formData.beds > 0,
    formData.baths > 0,
    formData.sqft.min != null || formData.sqft.max != null,
    formData.acres.min != null || formData.acres.max != null,
    formData.propertyTypes.length > 0,
    formData.propertyFeatures.length > 0,
  ].filter(Boolean).length;

  const openSaveSearch = () => {
    if (saving) return;

    // Signed-out visitors must log in first. Send them to the member area;
    // Umbraco public access bounces them to the login page.
    if (!mapConfig.isSignedIn) {
      const login = loginRedirectUrl();
      if (login) window.location.href = login;
      return;
    }

    setSaveError("");
    setSaveOpen(true);
  };

  const closeSaveSearch = useCallback(() => setSaveOpen(false), []);

  const saveSearch = async (title: string, frequency: AlertFrequency) => {
    setSaving(true);
    setSaveError("");
    const result = await saveSearchApi(title, formData, frequency);
    setSaving(false);

    if (result.status === "saved") {
      setSaveOpen(false);
      setSaved(true);
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
      saveTimer.current = window.setTimeout(() => setSaved(false), 1800);
    } else if (result.status === "unauthenticated") {
      // Session expired between page load and save: send to login.
      const login = loginRedirectUrl();
      if (login) window.location.href = login;
    } else {
      setSaveError("Sorry, we couldn't save your search. Please try again.");
    }
  };

  const shareSearch = async () => {
    const url = buildShareUrl(formData);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        // Legacy fallback for non-secure contexts
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.setAttribute("readonly", "");
        ta.style.position = "absolute";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      if (copyTimer.current) window.clearTimeout(copyTimer.current);
      copyTimer.current = window.setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      console.error("Copy failed:", err);
    }
  };

  useEffect(() => {
    return () => {
      if (copyTimer.current) window.clearTimeout(copyTimer.current);
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
    };
  }, []);

  return (
    <div className={`${styles.searchFormContainer} ${filtersOpen ? "" : styles.filtersCollapsed}`}>
      <section className={styles.optionsContainer}>
        <AiSearchBox />

        {/* Mobile only: CSS hides it from 768px up. */}
        <button
          type="button"
          className={styles.filtersToggle}
          onClick={() => setFiltersOpen((prev) => !prev)}
          aria-expanded={filtersOpen}
          aria-controls={`${filtersId} ${actionsId}`}
        >
          <span className={styles.filtersToggleIcon}>
            <FiltersIcon />
          </span>
          Filters
          {filterCount > 0 && (
            <span className={styles.filtersCount}>
              {filterCount}
              <span className={styles.visuallyHidden}> active</span>
            </span>
          )}
          <CarrotDownIcon className={`${styles.caret} ${filtersOpen ? styles.open : ""}`} />
        </button>

        {/* display: contents on desktop, so the layout there is unchanged. */}
        <div className={styles.filtersPanel} id={filtersId}>
          <section className={styles.rangeInputs}>
            <PriceOption
              min={formData.price.min}
              max={formData.price.max}
              onChange={(min, max) => setRange("price", { min, max })}
              labelAction={
                canClear && (
                  <button type="button" className={styles.clearFilters} onClick={clearAllFilters}>
                    <XIcon />
                    Clear Filters
                  </button>
                )
              }
            />

            <div className={styles.row}>
              <BedsOption value={formData.beds} onChange={(val) => setField("beds", val)} />
              <BathsOption value={formData.baths} onChange={(val) => setField("baths", val)} />
            </div>
          </section>

          <section className={styles.advancedFiltersContainer}>
            <Button
              className={styles.advancedToggle}
              icon={
                <CarrotDownIcon className={`${styles.caret} ${showAdvanced ? styles.open : ""}`} />
              }
              iconPosition="right"
              onClick={() => setShowAdvanced((prev) => !prev)}
              aria-expanded={showAdvanced}
            >
              Advanced Filters
            </Button>

            <div className={`${styles.advancedFiltersContent} ${showAdvanced ? styles.show : ""}`}>
              <SquareFeetOption
                min={formData.sqft.min}
                max={formData.sqft.max}
                onChange={(min, max) => setField("sqft", { min, max })}
              />

              <AcresOption
                min={formData.acres.min}
                max={formData.acres.max}
                onChange={(min, max) => setField("acres", { min, max })}
              />

              <PropertyTypeOption
                value={formData.propertyTypes}
                onChange={(val) => setField("propertyTypes", val as PropertyType[])}
              />

              <PropertyFeatureOption
                value={formData.propertyFeatures}
                onChange={(val) => setField("propertyFeatures", val as PropertyFeature[])}
              />
            </div>
          </section>
        </div>
      </section>

      <footer className={styles.footer} id={actionsId}>
        <Button
          icon={<StarIcon className={styles.icon} />}
          className={styles.actionSecondary}
          onClick={openSaveSearch}
          disabled={saving}
          aria-haspopup="dialog"
        >
          {saved ? "Saved!" : saving ? "Saving…" : "Save Search"}
        </Button>

        <Button
          icon={<span className={styles.icon}>{copied ? <CheckIcon /> : <ShareIcon />}</span>}
          className={`${styles.actionShare} ${copied ? styles.actionShareCopied : ""}`}
          onClick={shareSearch}
          aria-label={copied ? "Share link copied to clipboard" : "Copy share link"}
          title={copied ? "Link copied!" : "Copy share link"}
        />
      </footer>

      <SaveSearchModal
        open={saveOpen}
        saving={saving}
        error={saveError}
        onSave={saveSearch}
        onClose={closeSaveSearch}
      />
    </div>
  );
}
